import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Popconfirm from 'antd/lib/popconfirm';
import Radio from 'antd/lib/radio';
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnUnderline,
  Editor,
  EditorProvider,
  Toolbar,
} from 'react-simple-wysiwyg';
import {
  archiveArticle,
  createArticle,
  getArticleByIdentifier,
  publishArticle,
  unpublishArticle,
  updateArticle,
} from '../api/articles';
import { mapApiArticleToForm, mapArticleFormToPayload } from '../api/articles.mappers';
import FooterBar from '../components/helpdesk/FooterBar';
import HeaderBar from '../components/helpdesk/HeaderBar';
import '../styles/helpdesk.scss';

const { TextArea } = Input;

function CreateArticlePage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { articleId } = useParams();
  const isEditMode = Boolean(articleId);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [currentArticleStatus, setCurrentArticleStatus] = useState(null);

  useEffect(() => {
    if (!isEditMode || !articleId) return;

    async function loadArticle() {
      setLoadingArticle(true);
      try {
        const article = await getArticleByIdentifier(articleId);
        const formValues = mapApiArticleToForm(article);
        form.setFieldsValue(formValues);
        setContent(formValues.content);
        setCurrentArticleStatus(String(article?.status || '').toUpperCase());
      } catch {
        message.error('Unable to load article details.');
        navigate('/knowledge-base');
      } finally {
        setLoadingArticle(false);
      }
    }

    loadArticle();
  }, [articleId, form, isEditMode, navigate]);

  const handleSubmit = async (values) => {
    const payload = mapArticleFormToPayload(values, content);
    const shouldPublish = values.status === 'publish';

    setSubmitting(true);
    try {
      let savedArticleId = articleId;
      const wasPublished = currentArticleStatus === 'PUBLISHED';

      if (isEditMode && articleId) {
        await updateArticle(articleId, payload);
      } else {
        if (shouldPublish) {
          payload.status = 'DRAFT';
        }
        const createdArticle = await createArticle(payload);
        savedArticleId = createdArticle?.id || createdArticle?._id || createdArticle?.slug;
      }

      if (savedArticleId) {
        if (shouldPublish && (!isEditMode || !wasPublished)) {
          await publishArticle(savedArticleId);
        } else if (isEditMode && !shouldPublish && wasPublished) {
          await unpublishArticle(savedArticleId);
        }
      }

      message.success(isEditMode ? 'Article updated successfully.' : 'Article created successfully.');
      navigate('/knowledge-base');
    } catch {
      message.error('Unable to save article. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!articleId) return;

    setArchiving(true);
    try {
      await archiveArticle(articleId);
      message.success('Article archived successfully.');
      navigate('/knowledge-base');
    } catch {
      message.error('Unable to archive article. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <button type="button" className="create-article__back" onClick={() => navigate(-1)}>
            {'<-'} Go Back
          </button>

          <section className="create-article" aria-label="Create article form">
            <header className="create-article__header">
              <h1>{isEditMode ? 'Edit Article' : 'Create New Article'}</h1>
              <button
                type="button"
                aria-label="Close"
                className="create-article__close"
                onClick={() => navigate('/knowledge-base')}
              >
                x
              </button>
            </header>

            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              className="create-article__form"
              initialValues={{
                status: 'draft',
              }}
              onFinish={handleSubmit}
            >
              <Form.Item
                label="Article Title *"
                name="title"
                rules={[{ required: true, message: 'Please enter article title.' }]}
              >
                <Input placeholder="Enter a compelling title..." />
              </Form.Item>

              <Form.Item
                label="Excerpt *"
                name="excerpt"
                rules={[{ required: true, message: 'Please enter article excerpt.' }]}
              >
                <TextArea rows={4} placeholder="Write a brief summary..." />
              </Form.Item>

              <div className="create-article__row">
                <Form.Item
                  label="Author Name *"
                  name="author"
                  rules={[{ required: true, message: 'Please enter author name.' }]}
                >
                  <Input placeholder="Your name" />
                </Form.Item>
                <Form.Item label="Author Email" name="authorEmail">
                  <Input type="email" placeholder="author@example.com" />
                </Form.Item>
                <Form.Item
                  label="Category *"
                  name="category"
                  rules={[{ required: true, message: 'Please enter category.' }]}
                >
                  <Input placeholder="e.g. Legal, Security, Operations" />
                </Form.Item>
              </div>

              <Form.Item label="Featured Image URL (optional)" name="featuredImage">
                <Input placeholder="https://example.com/image.jpg" />
              </Form.Item>

              <Form.Item label="Tags (comma separated)" name="tags">
                <Input placeholder="helpdesk, support, process" />
              </Form.Item>

              <Form.Item
                label="Content *"
                name="content"
                rules={[
                  {
                    validator: async () => {
                      if (content && content.replace(/<[^>]*>/g, '').trim()) return;
                      throw new Error('Please enter article content.');
                    },
                  },
                ]}
              >
                <EditorProvider>
                  <div className="article-editor">
                    <Toolbar>
                      <BtnBold />
                      <BtnItalic />
                      <BtnUnderline />
                      <BtnBulletList />
                      <BtnNumberedList />
                      <BtnLink />
                      <BtnClearFormatting />
                    </Toolbar>
                    <Editor
                      value={content}
                      onChange={(event) => setContent(event.target.value)}
                      placeholder="Start writing your article..."
                    />
                  </div>
                </EditorProvider>
              </Form.Item>

              <Form.Item label="Status *" name="status" initialValue="draft">
                <Radio.Group>
                  <Radio value="draft">Save as Draft</Radio>
                  <Radio value="publish">Publish Now</Radio>
                </Radio.Group>
              </Form.Item>

              <div className="create-article__actions">
                <Button onClick={() => navigate('/knowledge-base')} disabled={submitting || archiving}>
                  Cancel
                </Button>
                {isEditMode ? (
                  <Popconfirm
                    title="Archive article"
                    description="Are you sure you want to archive this article?"
                    onConfirm={handleArchive}
                    okText="Archive"
                    okButtonProps={{ danger: true, loading: archiving }}
                    cancelButtonProps={{ disabled: archiving }}
                  >
                    <Button danger loading={archiving} disabled={submitting || loadingArticle}>
                      Archive
                    </Button>
                  </Popconfirm>
                ) : null}
                <Button type="primary" htmlType="submit" loading={submitting} disabled={loadingArticle}>
                  {isEditMode ? 'Save Changes' : 'Save Article'}
                </Button>
              </div>
            </Form>
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default CreateArticlePage;
